import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface HeroSectionProps {
  bgImage: string;
}

export default function HeroSection({ bgImage }: HeroSectionProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <section className="relative min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${bgImage})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/70 to-white/85" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-24 md:py-32">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-brand-50 border border-brand-200 text-brand-700 text-sm font-medium mb-6">
            {t('hero.eyebrow')}
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-warm-900 leading-tight mb-6">
            {t('hero.h1')}
          </h1>
          <p className="text-base md:text-lg text-warm-600 leading-relaxed mb-8 max-w-2xl mx-auto">
            {t('hero.description')}
          </p>

          <div className="max-w-xl mx-auto mb-8">
            <div className="flex items-center bg-white rounded-full border border-warm-200 shadow-sm px-2 py-2">
              <i className="ri-search-line text-warm-400 ml-3 text-lg" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('hero.searchPlaceholder')}
                className="flex-1 px-3 py-2 text-sm text-warm-800 placeholder:text-warm-400 outline-none bg-transparent"
              />
              <button className="px-5 py-2 bg-brand-600 text-white text-sm font-medium rounded-full hover:bg-brand-700 transition-colors whitespace-nowrap">
                {t('hero.ctaPrimary')}
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-6 py-3 bg-brand-600 text-white font-medium rounded-full hover:bg-brand-700 transition-colors whitespace-nowrap"
            >
              {t('hero.ctaMainSite')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-6 py-3 bg-warm-900 text-white font-medium rounded-full hover:bg-warm-800 transition-colors whitespace-nowrap"
            >
              {t('hero.ctaBuyNow')}
            </a>
            <a
              href="/pt/articles/cssbuy-spreadsheet-guide"
              className="px-6 py-3 border border-warm-300 text-warm-700 font-medium rounded-full hover:bg-warm-50 transition-colors whitespace-nowrap"
            >
              {t('hero.ctaSecondary')}
            </a>
          </div>

          <p className="text-xs text-warm-500">
            {t('hero.trust')}
          </p>
        </div>
      </div>
    </section>
  );
}